import {useEffect, useState} from 'react';
import {useMutation} from '@apollo/client';
import {useSelector} from 'react-redux';
import {Alert, Button, Input, InputNumber} from 'antd';
import {DeleteOutlined, PlusOutlined} from '@ant-design/icons';
import {toast} from 'react-toastify';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import {
  SET_SUPPORT_AVAILABILITY,
  SET_SUPPORT_RESPONSE_TARGET,
} from 'graphql/queries/mobileSettings';
import {makeSelectTabOperations} from 'redux/store/auth';
import {NavTabsEnum} from 'utils/constants';
import {
  MAX_WINDOWS,
  TIMEZONE,
  WEEKDAYS,
  describeSchedule,
  effectiveTarget,
  scheduleErrors,
  targetFromHours,
  toDays,
  toWeeklyHours,
} from './supportHoursUtils';
import styles from './mobileSetting.module.scss';

const SAVED_NOTE =
  'Saved. Reports pick up the change within about a minute; measured response times are never changed by it.';

const resultOf = response => response?.data?.updateAppConfig;

/**
 * Customer Support › response target and weekly support hours. Both live in
 * `aiSupport` and save through `updateAppConfig` under this page's existing
 * Platform Settings permission; each save sends only its own field.
 */
export const SupportResponseSettings = ({data, refetch}) => {
  const operations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.PlatformSettings),
  );
  const canEdit = Boolean(operations?.update);

  const storedTarget = effectiveTarget(data?.firstResponseTargetSeconds);
  const storedAvailability = data?.agentAvailability ?? null;

  const [hours, setHours] = useState(storedTarget / 3600);
  const [days, setDays] = useState(() => toDays(storedAvailability));
  const [confirm, setConfirm] = useState(null);

  const storedKey = JSON.stringify(storedAvailability);
  useEffect(() => setHours(storedTarget / 3600), [storedTarget]);
  useEffect(() => {
    setDays(toDays(storedAvailability));
  }, [storedKey]);

  const [saveTarget, targetState] = useMutation(SET_SUPPORT_RESPONSE_TARGET);
  const [saveHours, hoursState] = useMutation(SET_SUPPORT_AVAILABILITY);

  const target = targetFromHours(hours);
  const errors = scheduleErrors(days);
  const errorList = Object.values(errors).flat();
  const windowCount = toWeeklyHours(days).length;
  // With nothing stored, an empty editor is not a change: saving it would
  // quietly mark support as closed all week.
  const scheduleChanged = storedAvailability
    ? JSON.stringify(toWeeklyHours(days)) !==
      JSON.stringify(toWeeklyHours(toDays(storedAvailability)))
    : windowCount > 0;

  const run = async (mutate, variables) => {
    try {
      const node = resultOf(await mutate({variables}));
      if (node?.__typename === 'Error') {
        toast.error(node.message);
        return;
      }
      toast.success(SAVED_NOTE);
      setConfirm(null);
      refetch?.();
    } catch (error) {
      toast.error(error?.message || 'Update failed');
    }
  };

  const setWindow = (day, index, patch) =>
    setDays(current => ({
      ...current,
      [day]: current[day].map((window, i) =>
        i === index ? {...window, ...patch} : window,
      ),
    }));
  const addWindow = day =>
    setDays(current => ({
      ...current,
      [day]: [...current[day], {start: '08:00', end: '17:00'}],
    }));
  const removeWindow = (day, index) =>
    setDays(current => ({
      ...current,
      [day]: current[day].filter((_, i) => i !== index),
    }));
  const fillWeekdays = () =>
    setDays(
      Object.fromEntries(
        WEEKDAYS.map(day => [
          day.value,
          ['SATURDAY', 'SUNDAY'].includes(day.value)
            ? []
            : [{start: '08:00', end: '17:00'}],
        ]),
      ),
    );

  const current = describeSchedule(storedAvailability);

  return (
    <>
      <div className={styles.salesAgentCard}>
        <h3 className={styles.cardTitle}>First-response target</h3>
        <p className={styles.cardNote}>
          How quickly a support agent should send the first reply once a ticket
          reaches the human queue. One target for both products and both
          languages. Reports compare each agent&apos;s average against it.
        </p>
        <div className={styles.inlineField}>
          <InputNumber
            min={0.01}
            max={720}
            step={1}
            value={hours}
            onChange={setHours}
            disabled={!canEdit}
            className={styles.salesAgentInput}
            aria-label="Target in hours"
          />
          <span>hours</span>
        </div>
        {target.error && <p className={styles.fieldError}>{target.error}</p>}
        {data?.firstResponseTargetSeconds == null && (
          <p className={styles.cardNote}>
            Not set yet, so the default of 24 hours applies.
          </p>
        )}
        <Button
          type="primary"
          className={styles.brandButton}
          disabled={
            !canEdit || Boolean(target.error) || target.seconds === storedTarget
          }
          onClick={() => setConfirm('target')}>
          Save target
        </Button>
      </div>

      <div className={`${styles.salesAgentCard} ${styles.wideCard}`}>
        <h3 className={styles.cardTitle}>Support hours ({TIMEZONE})</h3>
        <p className={styles.cardNote}>
          When the support team is scheduled to be available, shown to customers
          in the app. Times are GMT (UTC+00:00 all year, no daylight saving).
          Days with no hours are closed. For an overnight shift, end at 24:00
          and continue the next day.
        </p>
        <p className={styles.cardNote}>
          <strong>Now:</strong>{' '}
          {current ? current.join(' · ') : 'Not set — customers see no hours.'}
        </p>

        <div className={styles.weekGrid}>
          {WEEKDAYS.map(day => (
            <div key={day.value} className={styles.dayRow}>
              <span className={styles.dayName}>{day.label}</span>
              <div className={styles.dayWindows}>
                {days[day.value].length === 0 && (
                  <span className={styles.closedTag}>Closed</span>
                )}
                {days[day.value].map((window, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <span key={index} className={styles.window}>
                    <Input
                      size="small"
                      value={window.start}
                      placeholder="08:00"
                      maxLength={5}
                      disabled={!canEdit}
                      onChange={event =>
                        setWindow(day.value, index, {
                          start: event.target.value,
                        })
                      }
                      aria-label={`${day.label} start`}
                    />
                    <span>–</span>
                    <Input
                      size="small"
                      value={window.end}
                      placeholder="17:00"
                      maxLength={5}
                      disabled={!canEdit}
                      onChange={event =>
                        setWindow(day.value, index, {end: event.target.value})
                      }
                      aria-label={`${day.label} end`}
                    />
                    {canEdit && (
                      <Button
                        size="small"
                        type="text"
                        icon={<DeleteOutlined />}
                        aria-label={`Remove ${day.label} ${window.start}–${window.end}`}
                        onClick={() => removeWindow(day.value, index)}
                      />
                    )}
                  </span>
                ))}
                {canEdit && (
                  <Button
                    size="small"
                    type="link"
                    className={styles.addHours}
                    icon={<PlusOutlined />}
                    disabled={windowCount >= MAX_WINDOWS}
                    onClick={() => addWindow(day.value)}>
                    Add hours
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>

        {errorList.length > 0 && (
          <Alert
            type="error"
            showIcon
            className={styles.scheduleErrors}
            message="Fix these before saving"
            description={
              <ul>
                {errorList.map(message => (
                  <li key={message}>{message}</li>
                ))}
              </ul>
            }
          />
        )}

        {canEdit && (
          <div className={styles.cardActions}>
            <Button
              type="primary"
              className={styles.brandButton}
              disabled={errorList.length > 0 || !scheduleChanged}
              onClick={() => setConfirm('hours')}>
              Save hours
            </Button>
            <Button onClick={fillWeekdays}>Fill Mon–Fri 08:00–17:00</Button>
            <Button
              danger
              disabled={!storedAvailability}
              onClick={() => setConfirm('clear')}>
              Clear schedule
            </Button>
          </div>
        )}
      </div>

      <ConfirmModal
        openModal={Boolean(confirm)}
        setOpenModal={open => !open && setConfirm(null)}
        loading={targetState.loading || hoursState.loading}
        title={
          confirm === 'clear'
            ? 'Clear the support hours?'
            : confirm === 'hours'
            ? 'Save the support hours?'
            : 'Save the response target?'
        }
        confirmText={
          confirm === 'target'
            ? `First-response target: ${
                storedTarget / 3600
              } hours → ${hours} hours. Measured response times don't change; only the comparison against the target does.`
            : confirm === 'hours'
            ? windowCount
              ? `This replaces the whole weekly schedule (${TIMEZONE}).`
              : 'No hours are set, so this marks support as closed all week.'
            : 'Customers will see no support hours until a new schedule is saved.'
        }
        handleOperation={() => {
          if (confirm === 'target') {
            run(saveTarget, {seconds: target.seconds});
          } else if (confirm === 'hours') {
            run(saveHours, {
              availability: {
                timezone: TIMEZONE,
                weeklyHours: toWeeklyHours(days),
              },
            });
          } else {
            run(saveHours, {availability: null});
          }
        }}
      />
    </>
  );
};
